import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Autocomplete,
  Button,
  ButtonBase,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

export const PRODUCTION_MODES = Object.freeze({
  FINAL: "FINAL_QUANTITY",
  INGREDIENT: "INGREDIENT_QUANTITY",
});

const UNIT_LABELS = Object.freeze({
  g: "g",
  ml: "ml",
  unit: "u",
  package: "paq",
});

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 40,
    borderRadius: "10px",
    bgcolor: "neutral.50",
    fontSize: 13,
    fontWeight: 650,
    "& fieldset": { borderColor: "neutral.700" },
    "&:hover fieldset": { borderColor: "primary.900" },
    "&.Mui-focused fieldset": { borderColor: "secondary.500" },
  },
};

function fixedUnit(unit) {
  return UNIT_LABELS[unit] || unit || "—";
}

function Selector({
  label,
  ariaLabel,
  value,
  options,
  loading,
  disabled,
  noOptionsText,
  onChange,
}) {
  return (
    <Stack spacing={0.6}>
      <Typography sx={{ color: "primary.900", fontSize: 12, fontWeight: 700 }}>
        {label}
      </Typography>
      <Autocomplete
        value={value}
        options={options}
        loading={loading}
        disabled={disabled}
        getOptionLabel={(option) => option?.name || ""}
        isOptionEqualToValue={(option, selected) =>
          Number(option?.id) === Number(selected?.id)
        }
        noOptionsText={noOptionsText}
        loadingText="Cargando..."
        clearText="Limpiar selección"
        openText="Mostrar opciones"
        closeText="Ocultar opciones"
        onChange={(_, option) => onChange(option)}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder="Buscar por nombre"
            inputProps={{ ...params.inputProps, "aria-label": ariaLabel }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  {loading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <SearchIcon sx={{ color: "primary.900", fontSize: 18 }} />
                  )}
                </InputAdornment>
              ),
            }}
            sx={fieldSx}
          />
        )}
      />
    </Stack>
  );
}

function ModeButton({ selected, disabled, children, onClick }) {
  return (
    <ButtonBase
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      onClick={onClick}
      sx={{
        flex: 1,
        minHeight: 54,
        px: 1.5,
        py: 1,
        borderRadius: "10px",
        bgcolor: selected ? "secondary.200" : "neutral.50",
        color: "primary.900",
        border: "1px solid",
        borderColor: selected ? "secondary.300" : "transparent",
        fontSize: 12,
        fontWeight: selected ? 800 : 650,
        lineHeight: 1.2,
        textAlign: "center",
        transition: "background-color .15s, border-color .15s",
        "&:hover": { bgcolor: selected ? "secondary.250" : "secondary.50" },
        "&.Mui-disabled": { opacity: 0.45 },
      }}
    >
      {children}
    </ButtonBase>
  );
}

function QuantityField({ label, ariaLabel, value, unit, disabled, onChange }) {
  const normalized = String(value ?? "").trim().replace(",", ".");
  const validFormat = /^\d+(?:\.\d{0,4})?$/.test(normalized);
  const invalidFormat = Boolean(value && !validFormat);
  const nonPositive = Boolean(
    value && validFormat && (!Number.isFinite(Number(normalized)) || Number(normalized) <= 0)
  );
  const invalid = invalidFormat || nonPositive;

  return (
    <Stack spacing={0.6}>
      <Typography sx={{ color: "primary.900", fontSize: 12, fontWeight: 700 }}>
        {label}
      </Typography>
      <TextField
        value={value}
        disabled={disabled}
        error={invalid}
        helperText={
          invalidFormat
            ? "Ingresá una cantidad con hasta 4 decimales."
            : nonPositive
            ? "La cantidad debe ser mayor a cero."
            : " "
        }
        size="small"
        onChange={(event) => onChange(event.target.value)}
        inputProps={{
          inputMode: "decimal",
          maxLength: 24,
          "aria-label": ariaLabel,
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">{fixedUnit(unit)}</InputAdornment>
          ),
        }}
        sx={{
          ...fieldSx,
          "& .MuiFormHelperText-root": { mx: 0, minHeight: 18, fontSize: 11 },
        }}
      />
    </Stack>
  );
}

export default function ProductionCalculationForm({
  product,
  productOptions,
  productsLoading,
  productsError,
  mode,
  ingredient,
  ingredientOptions,
  ingredientsLoading,
  ingredientsError,
  producedQuantity,
  basisQuantity,
  calculating,
  disabled = false,
  calculateError,
  canCalculate,
  onProductChange,
  onProductsRetry,
  onModeChange,
  onIngredientChange,
  onIngredientsRetry,
  onProducedQuantityChange,
  onBasisQuantityChange,
  onCalculate,
  onCancel,
}) {
  const ingredientMode = mode === PRODUCTION_MODES.INGREDIENT;

  return (
    <Stack spacing={1.6} sx={{ minWidth: 0 }}>
      <Typography
        component="h2"
        sx={{ color: "primary.900", fontSize: 18, fontWeight: 750 }}
      >
        Información General
      </Typography>

      <Selector
        label="Seleccionar elaborado"
        ariaLabel="Seleccionar elaborado"
        value={product}
        options={productOptions}
        loading={productsLoading}
        disabled={disabled || calculating}
        noOptionsText="No se encontraron elaborados disponibles"
        onChange={onProductChange}
      />

      {productsError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onProductsRetry}>
              Reintentar
            </Button>
          }
          sx={{ borderRadius: "14px", fontSize: 12, fontWeight: 650 }}
        >
          {productsError}
        </Alert>
      ) : null}

      <Stack spacing={0.75}>
        <Typography sx={{ color: "primary.900", fontSize: 12, fontWeight: 700 }}>
          Forma de producción
        </Typography>
        <Stack direction="row" spacing={1}>
          <ModeButton
            selected={!ingredientMode}
            disabled={disabled || calculating}
            onClick={() => onModeChange(PRODUCTION_MODES.FINAL)}
          >
            Calcular según producción final
          </ModeButton>
          <ModeButton
            selected={ingredientMode}
            disabled={disabled || calculating || !product?.supportsIngredientQuantity}
            onClick={() => onModeChange(PRODUCTION_MODES.INGREDIENT)}
          >
            Calcular según insumo
          </ModeButton>
        </Stack>
        {product && !product.supportsIngredientQuantity ? (
          <Typography sx={{ color: "neutral.600", fontSize: 11, fontWeight: 650 }}>
            Este elaborado no tiene insumos directos para usar como base.
          </Typography>
        ) : null}
      </Stack>

      {ingredientMode ? (
        <>
          <Selector
            label="Seleccionar insumo"
            ariaLabel="Seleccionar insumo directo"
            value={ingredient}
            options={ingredientOptions}
            loading={ingredientsLoading}
            disabled={disabled || !product || calculating}
            noOptionsText="No hay insumos directos disponibles"
            onChange={onIngredientChange}
          />
          {ingredientsError ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={onIngredientsRetry}>
                  Reintentar
                </Button>
              }
              sx={{ borderRadius: "14px", fontSize: 12, fontWeight: 650 }}
            >
              {ingredientsError}
            </Alert>
          ) : null}
          <QuantityField
            label="Cantidad del insumo"
            ariaLabel="Cantidad del insumo"
            value={basisQuantity}
            unit={ingredient?.unit}
            disabled={disabled || !ingredient || calculating}
            onChange={onBasisQuantityChange}
          />
        </>
      ) : (
        <QuantityField
          label="Cantidad a producir"
          ariaLabel="Cantidad a producir"
          value={producedQuantity}
          unit={product?.resultUnit}
          disabled={disabled || !product || calculating}
          onChange={onProducedQuantityChange}
        />
      )}

      {calculateError ? (
        <Alert severity="error" sx={{ borderRadius: "14px", fontSize: 12, fontWeight: 650 }}>
          {calculateError}
        </Alert>
      ) : null}

      <Stack
        role="group"
        aria-label="Acciones de producción"
        direction={{ xs: "column", sm: "row" }}
        spacing={1.25}
        sx={{
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
        }}
      >
        <Button
          type="button"
          variant="contained"
          disabled={disabled || !canCalculate || calculating}
          onClick={onCalculate}
          startIcon={
            calculating ? <CircularProgress size={16} color="inherit" /> : null
          }
          sx={{
            minWidth: 150,
            height: 38,
            px: 2.4,
            borderRadius: "33px",
            bgcolor: "primary.500",
            color: "logoblanco",
            textTransform: "none",
            fontSize: 12,
            fontWeight: 800,
            boxShadow: "0px 4px 12px rgba(23, 31, 76, 0.18)",
            "&:hover": { bgcolor: "primary.600" },
            "&.Mui-disabled": {
              bgcolor: "secondary.100",
              color: "neutral.500",
            },
          }}
        >
          {calculating ? "Calculando..." : "Calcular"}
        </Button>

        <Button
          type="button"
          variant="contained"
          disabled={disabled || calculating}
          onClick={onCancel}
          sx={{
            minWidth: 150,
            height: 38,
            px: 2.4,
            borderRadius: "33px",
            bgcolor: "secondary.50",
            color: "primary.900",
            textTransform: "none",
            fontWeight: 800,
            fontSize: 12,
            boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
            "&:hover": { bgcolor: "secondary.100" },
            "&.Mui-disabled": {
              bgcolor: "secondary.50",
              color: "neutral.500",
              opacity: 0.7,
            },
          }}
        >
          Cancelar
        </Button>
      </Stack>
    </Stack>
  );
}
